import React, { useEffect, useMemo, useState } from "react";
import Navbar from "@/components/storefront/Navbar";
import Footer from "@/components/storefront/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarDays, Clock, CreditCard, GraduationCap } from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import { api } from "@/lib/api";
import { formatSAR } from "@/lib/i18n";
import { toast } from "sonner";

const WEEKDAY_ORDER = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

function dateToISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function jsWeekdayToName(jsWeekday) {
  // JS: Sun=0..Sat=6
  return WEEKDAY_ORDER[jsWeekday];
}

function hhmm(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function formatHour(h, lang) {
  if (lang === "ar") return `${h}:00`;
  const ampm = h >= 12 && h < 24 ? "PM" : "AM";
  const display = h === 0 || h === 24 ? 12 : h > 12 ? h - 12 : h;
  return `${display}:00 ${ampm}`;
}

export default function Course() {
  const { t, lang } = useApp();
  const [course, setCourse] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [takenSlots, setTakenSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [duration, setDuration] = useState(30);
  const [slotStart, setSlotStart] = useState(null); // minutes from midnight
  const [form, setForm] = useState({ name: "", phone: "", email: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);

  useEffect(() => {
    api.get("/courses").then((r) => setCourse((r.data || [])[0])).catch(() => {});
    api.get("/availability").then((r) => setAvailability(r.data?.weekdays || {})).catch(() => {});
  }, []);

  useEffect(() => {
    if (!course || !selectedDate) return;
    const iso = dateToISO(selectedDate);
    api
      .get(`/bookings/slots`, { params: { course_id: course.id, booking_date: iso } })
      .then((r) => setTakenSlots(r.data || []))
      .catch(() => setTakenSlots([]));
    setSlotStart(null);
  }, [course, selectedDate]);

  const dayName = selectedDate ? jsWeekdayToName(selectedDate.getDay()) : null;
  const dayCfg = availability && dayName ? availability[dayName] : null;
  const dayEnabled = !!dayCfg?.enabled;

  const candidateSlots = useMemo(() => {
    if (!dayEnabled || !dayCfg) return [];
    const openMin = Number(dayCfg.open) * 60;
    const closeMin = Number(dayCfg.close) * 60;
    const slots = [];
    // 30-min granularity
    for (let m = openMin; m + duration <= closeMin; m += 30) {
      const taken = takenSlots.some((s) => m < s.end && s.start < m + duration);
      slots.push({ start: m, taken });
    }
    return slots;
  }, [dayEnabled, dayCfg, duration, takenSlots]);

  const price = duration * Number(course?.price_per_minute || 1);
  const vat = price * 0.15;
  const total = price + vat;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDate) {
      toast.error(t.pickDay);
      return;
    }
    if (!dayEnabled) {
      toast.error(lang === "ar" ? "اليوم غير متاح" : "Day not available");
      return;
    }
    if (slotStart === null) {
      toast.error(t.pickTime);
      return;
    }
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error(lang === "ar" ? "الاسم والجوال مطلوبان" : "Name and phone required");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings/checkout", {
        course_id: course.id,
        booking_date: dateToISO(selectedDate),
        slot_start: hhmm(slotStart),
        duration_minutes: duration,
        customer_name: form.name,
        customer_mobile: form.phone,
        customer_email: form.email,
        notes: form.notes,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Booking failed");
      setSubmitting(false);
    }
  };

  if (!course) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center text-muted-foreground">Loading...</div>
      </div>
    );
  }

  const name = lang === "ar" ? course.name_ar : course.name_en;
  const desc = lang === "ar" ? course.description_ar : course.description_en;

  const durations = [
    { val: 30, label: t.duration30 },
    { val: 60, label: t.duration60 },
    { val: 90, label: t.duration90 },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-1 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 w-full">
        <div className="grid lg:grid-cols-2 gap-10">
          {/* Left: course info */}
          <div className="space-y-5">
            <div className="aspect-[4/3] rounded-xl overflow-hidden bg-muted border border-border">
              <img src={course.preview_image} alt={name} className="h-full w-full object-cover" />
            </div>
            <div className="inline-flex items-center gap-2 bg-[#FFD60A] text-black rounded-full px-3 py-1 text-xs font-bold uppercase tracking-widest">
              <GraduationCap className="h-3.5 w-3.5" />
              {course.price_per_minute} {t.pricePerMin}
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">{name}</h1>
            <p className="text-muted-foreground leading-relaxed">{desc}</p>
          </div>

          {/* Right: booking form */}
          <form
            onSubmit={onSubmit}
            data-testid="booking-form"
            className="bg-surface border border-border rounded-xl p-6 space-y-5 h-fit"
          >
            <h2 className="font-bold text-lg flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-primary" />
              {t.bookCourse}
            </h2>

            {/* Duration */}
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground font-bold">{t.pickDuration}</Label>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {durations.map((d) => (
                  <button
                    key={d.val}
                    type="button"
                    onClick={() => setDuration(d.val)}
                    data-testid={`duration-${d.val}`}
                    className={`px-3 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                      duration === d.val
                        ? "border-primary bg-accent text-accent-foreground shadow-sm"
                        : "border-border bg-surface hover:bg-muted"
                    }`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Day picker */}
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground font-bold">{t.pickDay}</Label>
              <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="day-picker-trigger"
                    className="mt-2 w-full justify-start gap-2 font-normal"
                  >
                    <CalendarDays className="h-4 w-4 text-muted-foreground" />
                    {selectedDate
                      ? selectedDate.toLocaleDateString(lang === "ar" ? "ar-SA" : "en-GB", {
                          weekday: "long", day: "numeric", month: "long",
                        })
                      : t.pickDay}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(d) => {
                      if (d) {
                        setSelectedDate(d);
                        setPopoverOpen(false);
                      }
                    }}
                    disabled={(d) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      // 1) no past dates
                      if (d < today) return true;
                      // 2) only within the current calendar month
                      const now = new Date();
                      if (d.getMonth() !== now.getMonth() || d.getFullYear() !== now.getFullYear()) return true;
                      // 3) skip days that admin disabled
                      const dayName = jsWeekdayToName(d.getDay());
                      const cfg = availability?.[dayName];
                      if (!cfg || !cfg.enabled) return true;
                      return false;
                    }}
                  />
                </PopoverContent>
              </Popover>
              {selectedDate && !dayEnabled && (
                <p className="text-xs text-destructive mt-2" data-testid="day-disabled-msg">
                  {lang === "ar" ? "اليوم غير متاح للحجز" : "This day is not available"}
                </p>
              )}
              {selectedDate && dayEnabled && (
                <p className="text-xs text-muted-foreground mt-2">
                  {t.open} {formatHour(dayCfg.open, lang)} · {t.close}{" "}
                  {formatHour(dayCfg.close, lang)}
                </p>
              )}
            </div>

            {/* Time slots */}
            {selectedDate && dayEnabled && (
              <div data-testid="time-slots-block">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground font-bold">{t.pickTime}</Label>
                <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-64 overflow-auto pe-1">
                  {candidateSlots.map((s) => (
                    <button
                      key={s.start}
                      type="button"
                      disabled={s.taken}
                      onClick={() => setSlotStart(s.start)}
                      data-testid={`slot-${s.start}`}
                      className={`px-2 py-2 rounded-md text-xs font-medium font-mono transition-all border ${
                        s.taken
                          ? "border-border bg-muted text-muted-foreground/50 line-through cursor-not-allowed"
                          : slotStart === s.start
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-surface hover:bg-accent"
                      }`}
                    >
                      {hhmm(s.start)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Customer info */}
            <div className="space-y-3 pt-3 border-t border-border">
              <div>
                <Label htmlFor="bn" className="text-xs">{t.yourName} *</Label>
                <Input id="bn" data-testid="booking-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div>
                <Label htmlFor="bp" className="text-xs">{t.yourPhone} *</Label>
                <Input id="bp" data-testid="booking-phone" placeholder="+9665..." value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
              </div>
              <div>
                <Label htmlFor="be" className="text-xs">{t.yourEmail}</Label>
                <Input id="be" type="email" data-testid="booking-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="bnotes" className="text-xs">{t.notesLabel}</Label>
                <Input id="bnotes" data-testid="booking-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>

            {/* Summary */}
            <div className="space-y-1 pt-3 border-t border-border text-sm">
              <Row label={t.subtotal} value={formatSAR(price, lang)} />
              <Row label={t.vat} value={formatSAR(vat, lang)} />
              <div className="flex justify-between text-base font-bold pt-2 border-t border-border">
                <span>{t.total}</span>
                <span data-testid="booking-total">{formatSAR(total, lang)}</span>
              </div>
            </div>

            <Button type="submit" size="lg" className="w-full gap-2" disabled={submitting} data-testid="booking-submit">
              <CreditCard className="h-4 w-4" />
              {submitting ? "..." : t.bookAndPay}
            </Button>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium">{value}</span>
  </div>
);
